import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Icon from './Icon';
import { getAccessToken, isSecretaryContextUser } from '../authStorage';
import { fetchBranches } from '../branchesApi';
import { createShareholder, fetchShareholderById, mapShareholderFormToPayload, updateShareholder } from '../shareholdersApi';
import {
  BANK_OPTIONS,
  EMPTY_SHAREHOLDER_FORM,
  SHAREHOLDER_TYPE_OPTIONS,
  SHARE_STOCK_TYPE_OPTIONS,
  getSampleShareholderForm,
  normalizeShareholderType,
  normalizeShareStockType,
  shareholderTypeLabel,
  shareStockTypeLabel,
  validateShareholderForm,
} from '../shareholderFormUtils';
import { useLanguage } from '../i18n/language';
import { useT } from '../i18n/translations';
import './BranchesPage.css';
import './BoardPage.css';
import './DecisionFormPage.css';

function ShareholderFormPage({ mode }) {
  const { language, direction } = useLanguage();
  const t = useT();

  const navigate = useNavigate();
  const { id: idParam } = useParams();
  const isNew = mode === 'new';
  const editId = isNew ? null : Number(idParam);
  const invalidEditId = !isNew && (!Number.isFinite(editId) || editId < 1);
  const [branches, setBranches] = useState([]);
  const [form, setForm] = useState(EMPTY_SHAREHOLDER_FORM);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [accessDenied, setAccessDenied] = useState(false);
  const [step, setStep] = useState(0);

  const bankOptionLabels = useMemo(
    () => BANK_OPTIONS.map((b) => ((language === 'ar' || language === 'ur') ? b.ar : b.en)),
    [language],
  );

  useEffect(() => {
    if (!getAccessToken()) {
      navigate('/login', { replace: true });
      return;
    }
    if (!isSecretaryContextUser()) {
      setAccessDenied(true);
      setLoading(false);
      return;
    }
    if (invalidEditId) {
      setLoadError(t('shareholders.invalidId'));
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError('');
      try {
        const branchRows = await fetchBranches();
        if (cancelled) return;
        setBranches(branchRows);
        const firstBranchId = branchRows[0]?.id ? String(branchRows[0].id) : '';
        if (isNew) {
          setForm({ ...EMPTY_SHAREHOLDER_FORM, branchId: firstBranchId });
        } else {
          const item = await fetchShareholderById(editId);
          if (cancelled) return;
          setForm({
            name: item.name,
            branchId: String(item.branchId),
            ownership: String(item.ownership),
            type: normalizeShareholderType(item.type),
            shareStockType: normalizeShareStockType(item.shareStockType),
            phone: item.phone || '',
            email: item.email || '',
            bankName: item.bankName || '',
            bankAccountNumber: item.bankAccountNumber || '',
            iban: item.iban || '',
            userName: item.userName || '',
            password: '',
            status: item.status,
          });
        }
      } catch (e) {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : t('shareholders.loadFailed'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [navigate, isNew, editId, invalidEditId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e) => {
    setErrors((p) => ({ ...p, [e.target.name]: '', _api: '' }));
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
  };

  const handleFillSample = () => {
    const firstId = branches[0]?.id != null ? String(branches[0].id) : '';
    setErrors({});
    setForm(getSampleShareholderForm(form.branchId || firstId));
  };

  const handleSave = async () => {
    const errs = validateShareholderForm(form, isNew, {
      nameRequired: t('shareholders.valName'),
      branchRequired: t('shareholders.valBranch'),
      ownershipRequired: t('shareholders.valOwnership'),
      ownershipRange: t('shareholders.valOwnershipRange'),
      emailInvalid: t('shareholders.valEmail'),
      userNameRequired: t('shareholders.valUserName'),
      passwordRequired: t('shareholders.valPassword'),
      passwordMin: t('shareholders.valPasswordMin'),
    });
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setSaving(true);
    try {
      const payload = mapShareholderFormToPayload(form);
      if (isNew) await createShareholder(payload);
      else await updateShareholder(editId, payload);
      navigate('/app/shareholders');
    } catch (e) {
      setErrors({ _api: e instanceof Error ? e.message : t('shareholders.saveFailed') });
    } finally {
      setSaving(false);
    }
  };

  const validateInfoStep = () => {
    const errs = {};
    if (!form.name?.trim()) errs.name = t('shareholders.valName');
    if (!form.branchId) errs.branchId = t('shareholders.valBranch');
    if (!form.ownership) errs.ownership = t('shareholders.valOwnership');
    setErrors((p) => ({ ...p, ...errs }));
    return Object.keys(errs).length === 0;
  };

  const onNext = () => {
    if (step === 0 && !validateInfoStep()) return;
    setStep((s) => Math.min(2, s + 1));
  };

  if (accessDenied) return <p style={{ padding: 24, color: '#78909c', fontFamily: 'Cairo, sans-serif' }}>{t('common.noPerm')}</p>;

  const lblPasswordNew = <>{t('auth.password')} <span>*</span></>;
  const lblPasswordEdit = t('auth.password');

  return (
    <div className="decision-v2" dir={direction}>
      <div className="page-wrapper">
      {loadError && <div className="v2-error">{loadError}</div>}
      {loading && <div className="v2-loading">{t('shareholders.loading')}</div>}

      {!loading && !loadError && (
        <div className="card">
          <div className="card-top">
            <div className="top-bar top-bar--single-line">
              <div className="page-title-row">
                <h1 className="page-title">{isNew ? t('shareholders.titleNew') : t('shareholders.titleEdit')}<span className="page-title-icon"><Icon name="money" size={16} color="#0EA5A0" /></span></h1>
              </div>
              <div className="action-btns">
                {isNew && <button type="button" className="btn btn-outline" onClick={handleFillSample} disabled={saving}>{t('shareholders.fillSample')}</button>}
                <button type="button" className="btn btn-ghost" onClick={() => navigate('/app/shareholders')}>{t('common.cancel')}</button>
                {step < 2 ? (
                  <button type="button" className="btn btn-primary" onClick={onNext}>{t('shareholders.nextBtn')}</button>
                ) : (
                  <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving || loading || !!loadError}>
                    {saving ? t('shareholders.saving') : isNew ? t('shareholders.submitNew') : t('shareholders.submitEdit')}
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className="card-inner">
          <div style={{ display: 'flex', flexDirection: 'row', gap: 10, marginBottom: 18, alignItems: 'stretch' }}>
            {[t('shareholders.stepBasic'), t('shareholders.stepAccount'), t('shareholders.stepReview')].map((label, i) => {
              const done = i < step;
              const active = i === step;
              return (
                <div key={label} style={{ textAlign: 'center', flex: 1, color: active || done ? '#0EA5A0' : '#8AACAC', fontSize: '0.82rem' }}>
                  <button type="button" onClick={() => setStep(i)} style={{ width: '100%', border: `1.5px solid ${active || done ? '#0EA5A0' : '#DDE6E6'}`, background: active ? '#E0F7F6' : '#fff', padding: '10px 8px', fontWeight: 800, position: 'relative', cursor: 'pointer', color: 'inherit', fontFamily: 'Cairo, sans-serif' }}>
                    <span style={{ display: 'inline-block', minWidth: 20, marginInlineEnd: 6 }}>{done ? '✓' : String(i + 1)}</span>{label}
                    <span style={{ position: 'absolute', right: 0, left: 0, bottom: -1, height: 3, background: active ? '#0EA5A0' : 'transparent' }} />
                  </button>
                </div>
              );
            })}
          </div>
          <div className="mform">
            {errors._api && <div style={{ marginBottom: 12, padding: '10px 12px', background: '#ffebee', color: '#c62828', borderRadius: 8 }}>{errors._api}</div>}
            {step === 0 && <div className="mform__row mform__row--4">
              <div className="mform__field">
                <label className="mform__label">{t('shareholders.lblName')} <span>*</span></label>
                <div className="mform__input-wrap"><Icon name="user" size={14} color="#02c3a6" className="mform__input-icon" /><input className={`mform__input mform__input--with-icon${errors.name ? ' mform__input--error' : ''}`} name="name" value={form.name} onChange={handleChange} /></div>
                {errors.name && <span className="mform__error">{errors.name}</span>}
              </div>
              <div className="mform__field">
                <label className="mform__label">{t('shareholders.lblType')}</label>
                <div className="mform__input-wrap"><Icon name="money" size={14} color="#1b8eff" className="mform__input-icon" /><select className="mform__select mform__input--with-icon" name="type" value={form.type} onChange={handleChange}>{SHAREHOLDER_TYPE_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{shareholderTypeLabel(opt.value, language)}</option>)}</select></div>
              </div>
              <div className="mform__field">
                <label className="mform__label">{t('shareholders.lblStock')}</label>
                <div className="mform__input-wrap"><Icon name="chart" size={14} color="#8b5cf6" className="mform__input-icon" /><select className="mform__select mform__input--with-icon" name="shareStockType" value={form.shareStockType} onChange={handleChange}>{SHARE_STOCK_TYPE_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{shareStockTypeLabel(opt.value, language)}</option>)}</select></div>
              </div>
              <div className="mform__field">
                <label className="mform__label">{t('shareholders.lblOwnership')} <span>*</span></label>
                <div className="mform__input-wrap"><Icon name="pie-chart" size={14} color="#ff8c42" className="mform__input-icon" /><input className={`mform__input mform__input--with-icon${errors.ownership ? ' mform__input--error' : ''}`} name="ownership" type="number" min="0.1" max="100" step="0.1" value={form.ownership} onChange={handleChange} dir="ltr" /></div>
                {errors.ownership && <span className="mform__error">{errors.ownership}</span>}
              </div>
            </div>}
            {step === 0 && <div className="mform__row mform__row--4">
              <div className="mform__field mform__field--span3">
                <label className="mform__label">{t('shareholders.lblBranch')} <span>*</span></label>
                <div className="mform__input-wrap"><Icon name="branch" size={14} color="#0ea5a4" className="mform__input-icon" /><select className={`mform__select mform__input--with-icon${errors.branchId ? ' mform__select--error' : ''}`} name="branchId" value={form.branchId} onChange={handleChange}><option value="">{t('shareholders.pickBranch')}</option>{branches.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
                {errors.branchId && <span className="mform__error">{errors.branchId}</span>}
              </div>
              <div className="mform__field">
                <label className="mform__label">{t('common.status')}</label>
                <div className="mform__input-wrap"><Icon name="settings" size={14} color="#14b8a6" className="mform__input-icon" /><select className="mform__select mform__input--with-icon" name="status" value={form.status} onChange={handleChange}><option value="active">{t('common.active')}</option><option value="inactive">{t('common.inactive')}</option></select></div>
              </div>
            </div>}
            {step === 0 && <div className="mform__row mform__row--4">
              <div className="mform__field">
                <label className="mform__label">{t('common.phone')}</label>
                <div className="mform__input-wrap"><Icon name="chat" size={14} color="#8b5cf6" className="mform__input-icon" /><input className="mform__input mform__input--with-icon" name="phone" value={form.phone} onChange={handleChange} dir="ltr" /></div>
              </div>
              <div className="mform__field">
                <label className="mform__label">{t('common.email')}</label>
                <div className="mform__input-wrap"><Icon name="chat" size={14} color="#ff8c42" className="mform__input-icon" /><input className={`mform__input mform__input--with-icon${errors.email ? ' mform__input--error' : ''}`} name="email" value={form.email} onChange={handleChange} dir="ltr" /></div>
                {errors.email && <span className="mform__error">{errors.email}</span>}
              </div>
            </div>}
            {step === 1 && <div className="mform__row mform__row--4">
              <div className="mform__field">
                <label className="mform__label">{t('shareholders.lblBankName')}</label>
                <div className="mform__input-wrap"><Icon name="branch" size={14} color="#0ea5a4" className="mform__input-icon" /><input className="mform__input mform__input--with-icon" name="bankName" value={form.bankName} onChange={handleChange} list="shareholder-bank-options" placeholder={t('shareholders.phBankName')} /></div>
              </div>
              <div className="mform__field">
                <label className="mform__label">{t('shareholders.lblBankAccountNumber')}</label>
                <div className="mform__input-wrap"><Icon name="money" size={14} color="#1b8eff" className="mform__input-icon" /><input className="mform__input mform__input--with-icon" name="bankAccountNumber" value={form.bankAccountNumber} onChange={handleChange} dir="ltr" /></div>
              </div>
              <div className="mform__field mform__field--span2">
                <label className="mform__label">{t('shareholders.lblIban')}</label>
                <div className="mform__input-wrap"><Icon name="chart" size={14} color="#8b5cf6" className="mform__input-icon" /><input className="mform__input mform__input--with-icon" name="iban" value={form.iban} onChange={handleChange} dir="ltr" /></div>
              </div>
            </div>}
            {step === 1 && <div className="mform__row mform__row--4">
              <div className="mform__field">
                <label className="mform__label">{t('shareholders.lblUserName')} {isNew && <span>*</span>}</label>
                <div className="mform__input-wrap"><Icon name="user" size={14} color="#0ea5a4" className="mform__input-icon" /><input className={`mform__input mform__input--with-icon${errors.userName ? ' mform__input--error' : ''}`} name="userName" value={form.userName} onChange={handleChange} dir="ltr" placeholder={t('shareholders.lblUserName')} /></div>
                {errors.userName && <span className="mform__error">{errors.userName}</span>}
              </div>
              <div className="mform__field">
                <label className="mform__label">{isNew ? lblPasswordNew : lblPasswordEdit}</label>
                <div className="mform__input-wrap"><Icon name="settings" size={14} color="#8b5cf6" className="mform__input-icon" /><input className={`mform__input mform__input--with-icon${errors.password ? ' mform__input--error' : ''}`} name="password" type="password" value={form.password} onChange={handleChange} dir="ltr" placeholder={isNew ? '••••••••' : t('board.wizLeaveBlank')} /></div>
                {errors.password && <span className="mform__error">{errors.password}</span>}
              </div>
            </div>}
            {step === 2 && (
              <div style={{ border: '1px solid #e4ecf7', padding: 14 }}>
                <div style={{ fontWeight: 800, color: '#00334a', marginBottom: 8 }}>{form.name || '—'}</div>
                <div style={{ color: '#607d8b', marginBottom: 4 }}>{t('shareholders.lblOwnership')}: {form.ownership || '—'}</div>
                <div style={{ color: '#607d8b', marginBottom: 4 }}>{t('shareholders.lblBranch')}: {branches.find((b) => String(b.id) === String(form.branchId))?.name || '—'}</div>
                <div style={{ color: '#607d8b', marginBottom: 4 }}>{t('shareholders.lblUserName')}: {form.userName || '—'}</div>
                <div style={{ color: '#607d8b', marginBottom: 4 }}>{t('common.email')}: {form.email || '—'}</div>
                <div style={{ color: '#607d8b' }}>{t('shareholders.lblBankName')}: {form.bankName || '—'}</div>
              </div>
            )}
          </div>
          </div>
        </div>
      )}
      <datalist id="shareholder-bank-options">
        {bankOptionLabels.map((label) => <option key={label} value={label} />)}
      </datalist>
      </div>
    </div>
  );
}

export default ShareholderFormPage;
